import { useEffect, useMemo } from 'react';

import type { LabelValue } from './serviceListFilters';
import { NodeType } from '../../@types/structure.type';
import { StructureTypeCategory } from '../../configs/enums';
import { useGetZones } from '../../queries/geosocial-query';
import { useGetRegionTypes } from '../../queries/regionTypes-query';
import {
  useGetServiceActionProcessTypes,
  useGetServiceBillingTypes,
} from '../../queries/service-query';
import { useGetServicesStatuses } from '../../queries/service-status-query';
import { useGetStructuresByTypeCategoryCodeAndTypeCode } from '../../queries/structure-query';

/**
 * Loads options for every list-type field in the services Add Filter modal
 * that has a known BFF source. Returned record is keyed by `FilterFieldDef.key`
 * and consumed by `AddFilterConfigure` to render a proper multiselect instead
 * of the legacy comma-separated text box. Fields without a source
 * (priorityCode, clientSLASummary, opsSLASummary, surveyColumns) are omitted
 * and fall back to the text input. Priority is intentionally excluded — it is
 * configured per client/contract/brand/service-type via
 * `/nodes/priorityselection` and has no global list to populate a flat
 * multiselect.
 */
export const useServiceFilterListOptions = (enabled: boolean): Record<string, LabelValue[]> => {
  // Pass `activeOnly=true` — without it the URL serialises to `?activeOnly=undefined`,
  // which the BFF rejects, returning empty arrays and leaving the dropdowns blank.
  const typesQuery = useGetServiceActionProcessTypes(true);
  const statusesQuery = useGetServicesStatuses();
  const billingTypesQuery = useGetServiceBillingTypes();
  const regionTypesQuery = useGetRegionTypes();
  const zonesQuery = useGetZones();
  const clientsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Client
  );
  const contractsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Contract
  );
  const brandsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Brand
  );

  // The status / client / contract / brand queries are declared with
  // `enabled: false` in the query hooks themselves (they back several screens
  // that need manual control), so we trigger the initial fetch once the modal
  // becomes active.
  const { refetch: refetchStatuses } = statusesQuery;
  const { refetch: refetchClients } = clientsQuery;
  const { refetch: refetchContracts } = contractsQuery;
  const { refetch: refetchBrands } = brandsQuery;
  useEffect(() => {
    if (!enabled) return;
    refetchStatuses();
    refetchClients();
    refetchContracts();
    refetchBrands();
  }, [enabled, refetchStatuses, refetchClients, refetchContracts, refetchBrands]);

  return useMemo(() => {
    const toActiveOptions = <T extends { name: string; isActive: boolean }>(
      items: T[] | undefined
    ): LabelValue[] =>
      (items ?? [])
        .filter((item) => item.isActive)
        .map((item) => ({ label: item.name, value: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const toNameOptions = <T extends { name: string }>(items: T[] | undefined): LabelValue[] =>
      (items ?? [])
        .map((item) => ({ label: item.name, value: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Build parentNodeId → clientCode lookup so contract options can display
    // "CLIENT_CODE - Contract Name". parentNodeId on a contract equals the
    // client's numeric id (as a string).
    const clientCodeByNodeId: Record<string, string> = {};
    for (const client of clientsQuery.data ?? []) {
      clientCodeByNodeId[String(client.id)] = client.code;
    }

    // Contract options — label shows "clientCode - contractName" for clarity
    // (Issue 3), value stays as the contract name the BFF expects, and
    // optionKey uses the contract's unique id to prevent React key collisions
    // when two contracts share the same name (Issue 2 / DOM accumulation fix).
    const contractNameOptions: LabelValue[] = (contractsQuery.data ?? [])
      .filter((c) => c.isActive)
      .map((c) => {
        const clientCode = clientCodeByNodeId[c.parentNodeId];
        return {
          label: clientCode ? `${clientCode} - ${c.name}` : c.name,
          value: c.name,
          optionKey: String(c.id),
          searchText: (clientCode ? `${clientCode} ${c.name}` : c.name).toLowerCase(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      serviceTypeName: toActiveOptions(typesQuery.data?.serviceTypes),
      // Action / process types from the bundled endpoint don't carry a
      // reliable `isActive` flag (matches the legacy filter util in
      // utils/services.ts). The BFF is already filtering to active when we
      // pass `activeOnly=true`, so map names directly without re-filtering.
      actionTypeName: toNameOptions(typesQuery.data?.actionTypes),
      processTypeName: toNameOptions(typesQuery.data?.processTypes),
      serviceStatusName: toActiveOptions(statusesQuery.data),
      billableType: toNameOptions(billingTypesQuery.data),
      clientName: toActiveOptions(clientsQuery.data),
      contractName: contractNameOptions,
      brandName: toActiveOptions(brandsQuery.data),
      regionType: toNameOptions(regionTypesQuery.data),
      serviceZone: toActiveOptions(zonesQuery.data),
      // Location Zone shares the same zone list as Service Zone (both filter
      // the geosocial zone set); the legacy CMS2 grid backed both with a
      // single `zoneList` singleSelect.
      locationZone: toActiveOptions(zonesQuery.data),
    };
  }, [
    typesQuery.data,
    statusesQuery.data,
    billingTypesQuery.data,
    clientsQuery.data,
    contractsQuery.data,
    brandsQuery.data,
    regionTypesQuery.data,
    zonesQuery.data,
  ]);
};
